import { lazy } from "react";

// React.lazy wrapper that survives a flaky chunk fetch (network blip, corrupted
// browser cache, antivirus/proxy mangling a gzip response, or a deploy that
// removed an old hashed chunk). Retries once, then does a single full reload;
// if the chunk still fails after that, the error propagates to
// ChunkErrorBoundary instead of looping.

const RELOAD_KEY = "chunk-reload-at";
const RELOAD_WINDOW_MS = 30 * 1000;

export function isChunkLoadError(error) {
    if (!error) return false;
    return (
        error.name === "ChunkLoadError" ||
        /Loading (CSS )?chunk [\w-]+ failed/i.test(error.message || "")
    );
}

function readReloadAt() {
    try {
        return Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
    } catch {
        return 0;
    }
}

function writeReloadAt(value) {
    try {
        if (value) window.sessionStorage.setItem(RELOAD_KEY, String(value));
        else window.sessionStorage.removeItem(RELOAD_KEY);
    } catch {
        // storage unavailable (private mode / blocked) — reload guard just won't persist
    }
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function lazyWithRetry(factory) {
    return lazy(async () => {
        try {
            return await factory();
        } catch (firstError) {
            if (!isChunkLoadError(firstError)) throw firstError;

            await wait(1000);
            try {
                return await factory();
            } catch (error) {
                if (!isChunkLoadError(error)) throw error;

                // One hard reload per window; a second failure shows the boundary.
                if (Date.now() - readReloadAt() > RELOAD_WINDOW_MS) {
                    writeReloadAt(Date.now());
                    window.location.reload();
                    // Keep Suspense fallback up while the page reloads.
                    return new Promise(() => {});
                }
                throw error;
            }
        }
    });
}
